// Display formatters used across modules. Localized to en-US for now.

import { formatDistanceToNowStrict, format } from "date-fns";

// Detect strings that already carry a timezone (Z or ±HH[:]MM at the tail).
const TZ_RE = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Parse a timestamp string, treating the unmarked SQLite form as UTC.
 *
 * Many of our worker columns are populated via SQLite's `datetime('now')`,
 * which returns `'YYYY-MM-DD HH:MM:SS'` — UTC, but with no `Z` and a
 * space instead of a `T`. JS then parses that as LOCAL time, producing a
 * UTC-vs-PT (~7-8h) drift everywhere a relative-time UI showed it. The
 * notification bell's "7 hours ago" stamp on a just-arrived email was
 * the most visible symptom. Coerce to UTC when no offset is present.
 */
export const parseTimestamp = (s: string): Date => {
  if (TZ_RE.test(s)) return new Date(s);
  return new Date(s.replace(" ", "T") + "Z");
};

export const fmtDate = (iso: string | null | undefined, fallback = "—"): string => {
  if (!iso) return fallback;
  try {
    return format(parseTimestamp(iso), "MMM d, yyyy");
  } catch {
    return fallback;
  }
};

export const fmtDateTime = (iso: string | null | undefined, fallback = "—"): string => {
  if (!iso) return fallback;
  try {
    return format(parseTimestamp(iso), "MMM d, yyyy h:mm a");
  } catch {
    return fallback;
  }
};

export const fmtRelative = (iso: string | null | undefined, fallback = "—"): string => {
  if (!iso) return fallback;
  try {
    return formatDistanceToNowStrict(parseTimestamp(iso), { addSuffix: true });
  } catch {
    return fallback;
  }
};

/**
 * Like fmtRelative but clamps future timestamps to "just now".
 * Use for events that already happened (sent, opened, clicked, etc.)
 * where a future timestamp is always bad data — never "in 2 hours".
 */
export const fmtRelativePast = (iso: string | null | undefined, fallback = "—"): string => {
  if (!iso) return fallback;
  try {
    const d = parseTimestamp(iso);
    if (d.getTime() > Date.now()) return "just now";
    return formatDistanceToNowStrict(d, { addSuffix: true });
  } catch {
    return fallback;
  }
};

export const fmtRelativeShort = (iso: string | null | undefined): string => {
  if (!iso) return "";
  try {
    return formatDistanceToNowStrict(parseTimestamp(iso));
  } catch {
    return "";
  }
};

export const fmtMoney = (
  cents: number | null | undefined,
  currency = "USD",
  opts: { showCents?: boolean } = {},
): string => {
  if (cents == null) return "—";
  const amount = cents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: opts.showCents ? 2 : 0,
    maximumFractionDigits: opts.showCents ? 2 : 0,
  }).format(amount);
};

export const fmtMoneyDollars = (dollars: number | null | undefined): string => {
  if (dollars == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(dollars);
};

export const fmtNumber = (n: number | null | undefined): string => {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
};

export const fmtPercent = (n: number | null | undefined, decimals = 0): string => {
  if (n == null) return "—";
  return `${n.toFixed(decimals)}%`;
};

/**
 * Format seconds as "Mm Ss" (or "Hh Mm" for >1h, or "Ns" for <1m).
 * Used for averageSessionDuration in the GA reports.
 */
export const fmtDuration = (seconds: number | null | undefined): string => {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "—";
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
};

/** Map a status / lifecycle / score string to a brand-colored badge variant. */
export const pillVariant = (status: string | null | undefined):
  "default" | "success" | "warn" | "danger" | "accent" | "muted" | "outline" => {
  if (!status) return "muted";
  const s = status.toLowerCase();
  if (["active", "signed", "approved", "completed", "warm", "hot", "delivered", "connected"].includes(s)) return "success";
  if (["draft", "pending", "pending_approval", "scheduled", "verified"].includes(s)) return "warn";
  if (["expired", "declined", "rejected", "stopped", "denied", "missing", "failed", "blazing"].includes(s)) return "danger";
  if (["sent", "open", "ready", "executing"].includes(s)) return "default";
  if (["nurture", "lead", "qualified", "customer", "evangelist"].includes(s)) return "accent";
  return "muted";
};
